export const GENERIC_HOOK_EVENT_TYPE = "uk.half-shot.matrix-hookshot.generic.hook";
export const OUTBOUND_HOOK_EVENT_TYPE = "uk.half-shot.matrix-hookshot.outbound-hook";

export type HookKind = "incoming" | "outgoing";

export interface Hook {
  id: string;
  kind: HookKind;
  name: string;
  url: string | null;
}

export type HookshotErrorKind = "forbidden" | "not_in_room" | "invalid_url" | "failed";

export class HookshotError extends Error {
  readonly kind: HookshotErrorKind;

  constructor(kind: HookshotErrorKind, message: string) {
    super(message);
    this.name = "HookshotError";
    this.kind = kind;
  }
}

export interface HookshotClientOptions {
  baseUrl: string;
  getOpenIdToken: () => Promise<{ accessToken: string; serverName: string }>;
}

const NOT_IN_ROOM_ERRCODE = "HS_NOT_IN_ROOM";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function stringField(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  return typeof value === "string" ? value : null;
}

function kindForEventType(eventType: string | null): HookKind | null {
  if (eventType === GENERIC_HOOK_EVENT_TYPE) return "incoming";
  if (eventType === OUTBOUND_HOOK_EVENT_TYPE) return "outgoing";
  return null;
}

function toHook(value: unknown): Hook | null {
  if (!isRecord(value)) return null;
  const id = stringField(value, "id");
  const kind = kindForEventType(stringField(value, "eventType"));
  if (!id || !kind) return null;
  const config = isRecord(value.config) ? value.config : {};
  const secrets = isRecord(value.secrets) ? value.secrets : {};
  const url = kind === "incoming" ? stringField(secrets, "url") : stringField(config, "url");
  return { id, kind, name: stringField(config, "name") ?? "", url };
}

export function isHttpsUrl(value: string): boolean {
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}

async function readJson(res: Response): Promise<unknown> {
  try {
    const parsed: unknown = await res.json();
    return parsed;
  } catch {
    return null;
  }
}

async function errorFor(res: Response): Promise<HookshotError> {
  const body = await readJson(res);
  const errcode = isRecord(body) ? stringField(body, "errcode") : null;
  if (errcode === NOT_IN_ROOM_ERRCODE) {
    return new HookshotError("not_in_room", "Hookshot is not in this room.");
  }
  if (res.status === 403) {
    return new HookshotError("forbidden", "You can't manage integrations here.");
  }
  return new HookshotError("failed", `Hookshot request failed (${res.status}).`);
}

export class HookshotClient {
  private readonly baseUrl: string;
  private readonly getOpenIdToken: HookshotClientOptions["getOpenIdToken"];
  private token: Promise<string> | null = null;

  constructor({ baseUrl, getOpenIdToken }: HookshotClientOptions) {
    this.baseUrl = baseUrl.replace(/\/+$/, "");
    this.getOpenIdToken = getOpenIdToken;
  }

  async listHooks(roomId: string): Promise<Hook[]> {
    const body = await this.request("GET", this.roomPath(roomId));
    if (!Array.isArray(body)) return [];
    return body.flatMap((item) => {
      const hook = toHook(item);
      return hook ? [hook] : [];
    });
  }

  async createIncoming(roomId: string, name: string): Promise<Hook> {
    return this.create(roomId, GENERIC_HOOK_EVENT_TYPE, { name });
  }

  async createOutgoing(roomId: string, name: string, url: string): Promise<Hook> {
    if (!isHttpsUrl(url)) {
      throw new HookshotError("invalid_url", "Outgoing webhooks need an https:// URL.");
    }
    return this.create(roomId, OUTBOUND_HOOK_EVENT_TYPE, { name, url });
  }

  async deleteHook(roomId: string, id: string): Promise<void> {
    await this.request("DELETE", `${this.roomPath(roomId)}/${encodeURIComponent(id)}`);
  }

  private roomPath(roomId: string): string {
    return `/${encodeURIComponent(roomId)}/connections`;
  }

  private async create(
    roomId: string,
    eventType: string,
    body: Record<string, string>,
  ): Promise<Hook> {
    const created = toHook(
      await this.request("POST", `${this.roomPath(roomId)}/${encodeURIComponent(eventType)}`, body),
    );
    if (!created) throw new HookshotError("failed", "Hookshot returned an unexpected response.");
    return created;
  }

  private async request(method: string, path: string, body?: unknown): Promise<unknown> {
    let res = await this.send(method, path, body, await this.currentToken());
    if (res.status === 401) {
      this.token = null;
      res = await this.send(method, path, body, await this.currentToken());
    }
    if (!res.ok) throw await errorFor(res);
    return readJson(res);
  }

  private send(method: string, path: string, body: unknown, token: string): Promise<Response> {
    return fetch(`${this.baseUrl}${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body === undefined ? {} : { "Content-Type": "application/json" }),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  }

  private currentToken(): Promise<string> {
    if (!this.token) {
      const pending = this.exchange();
      this.token = pending;
      pending.catch(() => {
        if (this.token === pending) this.token = null;
      });
    }
    return this.token;
  }

  private async exchange(): Promise<string> {
    const { accessToken, serverName } = await this.getOpenIdToken();
    const res = await fetch(`${this.baseUrl}/exchange_openid`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ matrixServer: serverName, openIdToken: accessToken }),
    });
    if (!res.ok) throw await errorFor(res);
    const body = await readJson(res);
    const token = isRecord(body) ? stringField(body, "token") : null;
    if (!token) throw new HookshotError("failed", "Hookshot did not return a token.");
    return token;
  }
}
